import { NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  Stethoscope,
  Users,
  Newspaper,
  ShoppingBag,
  UserCircle,
  ShieldCheck,
  Receipt,
  TrendingUp,
  Banknote,
  Package,
  CalendarClock,
  Tags,
  LayoutGrid,
  Pill,
  FlaskConical,
  Crown,
  Settings2,
  BellRing,
  ScrollText,
} from "lucide-react";
import { Logo } from "./Logo";
import { useAuthStore } from "@/lib/auth/store";
import { isAdmin } from "@/lib/auth/rbac";
import { cn } from "@/lib/utils";

type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
};

type NavGroup = { label: string | null; items: NavItem[] };

const GROUPS: NavGroup[] = [
  {
    label: null,
    items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard }],
  },
  {
    label: "People",
    items: [
      { to: "/doctors", label: "Doctors", icon: Stethoscope },
      { to: "/users", label: "Users", icon: Users },
      { to: "/roles", label: "Roles & permissions", icon: ShieldCheck },
    ],
  },
  {
    label: "Content",
    items: [
      { to: "/posts", label: "Posts", icon: Newspaper },
      { to: "/products", label: "Products", icon: ShoppingBag },
    ],
  },
  {
    label: "Catalog",
    items: [
      { to: "/catalog/specializations", label: "Specializations", icon: Tags },
      { to: "/catalog/categories", label: "Product categories", icon: LayoutGrid },
      { to: "/catalog/medicines", label: "Medicines", icon: Pill },
      { to: "/catalog/lab-tests", label: "Lab tests", icon: FlaskConical },
    ],
  },
  {
    label: "Clinical",
    items: [
      { to: "/appointments", label: "Appointments", icon: CalendarClock },
    ],
  },
  {
    label: "Finance",
    items: [
      { to: "/transactions", label: "Transactions", icon: Receipt },
      { to: "/orders", label: "Orders", icon: Package },
      { to: "/revenue", label: "Revenue", icon: TrendingUp },
      { to: "/withdrawals", label: "Withdrawals", icon: Banknote },
      { to: "/plans", label: "Subscription plans", icon: Crown },
    ],
  },
  {
    label: "Platform",
    items: [
      { to: "/settings", label: "App settings", icon: Settings2 },
      { to: "/notifications", label: "Push notifications", icon: BellRing },
      { to: "/audit", label: "Audit log", icon: ScrollText },
    ],
  },
  {
    label: "Account",
    items: [{ to: "/profile", label: "Profile", icon: UserCircle }],
  },
];

export function Sidebar() {
  const role = useAuthStore((s) => s.session?.role);
  const visible = isAdmin(role) ? GROUPS : [];

  return (
    <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-card">
      <div className="flex h-20 items-center justify-center px-5 border-b border-border">
        <Logo variant="wordmark" className="h-11" />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {visible.map((group, gi) => (
          <div key={group.label ?? gi} className={cn(gi > 0 && "mt-5")}>
            {group.label && (
              <p className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">
                {group.label}
              </p>
            )}
            <ul className="flex flex-col gap-1">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    className={({ isActive }) =>
                      cn(
                        "group relative flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors",
                        isActive
                          ? "text-primary-foreground"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <motion.div
                            layoutId="sidebar-active"
                            className="absolute inset-0 rounded-lg bg-gradient-sunset shadow-glow"
                            transition={{ type: "spring", stiffness: 380, damping: 30 }}
                          />
                        )}
                        <item.icon className={cn("size-4 shrink-0 relative", isActive && "text-primary-foreground")} />
                        <span className="relative">{item.label}</span>
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="border-t border-border p-4 text-[10px] text-muted-foreground">
        Afya24 Admin · v0.2.0 · {import.meta.env.MODE}
      </div>
    </aside>
  );
}
